import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  assetCatalog, type CatalogField, type CatalogKind, type CatalogVersionDetail, type CatalogVersionRow,
  type CatalogSlot, type CatalogTemplates, type Manufacturer, type ModelBody,
} from "@/api/client";
import { useConfirm } from "@/components/ConfirmDialog";
import { Field } from "@/components/Field";
import { ImageField } from "@/components/ImageCapture";
import { Modal } from "@/components/Modal";
import { MultiSelect } from "@/components/MultiSelect";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { Select, SearchInput } from "@/components/inputs";
import { CAP, useSession } from "@/auth/session";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { useStagedImage } from "@/lib/hooks/useStagedImage";

import { ActionRow } from "@/components/ActionRow";
import { Icon } from "@/components/icons";
/* ---------------- vocab ---------------- */
export const MODEL_CHIP: StatusMap = {
  draft: { label: "DRAFT", color: "var(--warn)" },
  active: { label: "ACTIVE", color: "var(--ok)" },
  retired: { label: "RETIRED", color: "var(--t-mute)" },
};
export const ModelChip = ({ st }: { st: string }) => <StatusChip st={st} map={MODEL_CHIP} />;

export const QK = ["asset-catalog"] as const;

/** Templates are static per deploy — fetched once, cached for the session. */
export function useTemplates() {
  return useQuery({ queryKey: [...QK, "templates"], queryFn: assetCatalog.templates, staleTime: Infinity });
}
export function useManufacturers() {
  return useQuery({ queryKey: [...QK, "manufacturers"], queryFn: assetCatalog.manufacturers });
}
/** All non-retired models — the option pool for pickers (catalog-sized: hundreds, not 100K). */
export function useModelPool() {
  return useQuery({ queryKey: [...QK, "models", "pool"],
    queryFn: () => assetCatalog.models({ limit: "1000" }) });
}
export function kindLabel(t: CatalogTemplates | undefined, id: string) {
  return t?.kinds.find((k) => k.id === id)?.label ?? id;
}

/** Hierarchical kind dropdown (optgroup per family). `only` restricts the pool. */
export function KindSelect({ kinds, value, onChange, only, placeholder = "— choose kind —", style }: {
  kinds: CatalogKind[]; value: string; onChange: (v: string) => void; only?: string[];
  placeholder?: string; style?: React.CSSProperties;
}) {
  const pool = only ? kinds.filter((k) => only.includes(k.id)) : kinds;
  const groups = Array.from(new Set(pool.map((k) => k.group)));
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} style={style}>
      <option value="">{placeholder}</option>
      {groups.map((g) => (
        <optgroup key={g} label={g}>
          {pool.filter((k) => k.group === g).map((k) => <option key={k.id} value={k.id}>{k.label}{k.abbr ? ` (${k.abbr})` : ""}</option>)}
        </optgroup>
      ))}
    </Select>
  );
}

/** Model dropdown: "name · v2 · Manufacturer (draft)" — retired never offered. */
export function ModelSelect({ rows, value, onChange, placeholder = "— choose model —", style }: {
  rows: CatalogVersionRow[]; value: string; onChange: (v: string) => void; placeholder?: string;
  style?: React.CSSProperties;
}) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} style={{ minWidth: 0, ...style }}>
      <option value="">{placeholder}</option>
      {rows.map((m) => (
        <option key={m.version_id} value={m.version_id}>
          {m.name} · v{m.version} · {m.manufacturer}{m.status === "draft" ? " (draft)" : ""}
        </option>
      ))}
    </Select>
  );
}


/** Tiny thumbnail for tables / pickers; a dashed box when there is none. */
export function Thumb({ src, size = 28, radius = 6 }: { src: string | null; size?: number; radius?: number }) {
  return src
    ? <img src={src} alt="" style={{ width: size, height: size, objectFit: "cover", borderRadius: radius, border: "1px solid var(--line2)", verticalAlign: "middle", background: "#fff" }} />
    : <span style={{ display: "inline-block", width: size, height: size, borderRadius: radius, border: "1px dashed var(--line2)", verticalAlign: "middle" }} />;
}

const numOf = (v: unknown) => {
  const n = parseFloat(String(v ?? "")); return Number.isFinite(n) ? String(n).replace(".", "p") : "";
};
const vOf = (v: unknown) => String(v ?? "").replace(/\s*V$/i, "").trim();

/** Hover explanation of the naming rule — what is fixed and what is yours. */
export function nameRuleTitle(abbr: string, fam: string, ble: boolean): string {
  return [
    `Naming rule: ${abbr}-${fam}${ble ? "-<rating>-B" : "-<rating>"}-<suffix>`,
    `${abbr} = manufacturer code (fixed, from Manufacturers)`,
    `${fam} = product family (fixed, from Kind — connected kinds have their own code: BMSC, VCUC, CLUC, TCU)`,
    "rating = key specs above (fixed - changes when you edit the specs)",
    `B flag = ${ble ? "present (BLE tick)" : "absent (no BLE tick)"}`,
    "suffix = YOURS to type - hardware revision (R1, R2...); a new PCB spin means a new suffix",
  ].join("\n");
}

/** Technical-name prefix per ASSET_CATALOG.md §2a: ABBR-FAM-RATING-FLAGS-.
 *  Everything is derived from what the form already holds; the user types
 *  only the final segment (hw rev). */
export function namePrefix(kind: string, specs: Record<string, string>, abbr: string, fam?: string): string {
  const parts = [abbr, fam || kind.toUpperCase()];
  // rating: only emit segments whose values are actually present — no
  // dangling unit letters (a cleanup regex here once ate the S of SEG-LCD)
  let rating = "";
  if (kind === "bms" || kind === "bms_c") {
    const [se, a] = [numOf(specs.series_max), numOf(specs.cont_a)];
    rating = se && a ? `${se}S${a}` : se ? `${se}S` : "";
  } else if (kind === "mcu") {
    const [v, a] = [vOf(specs.volt), numOf(specs.cont_a)];
    rating = v && a ? `${v}V${a}` : v ? `${v}V` : "";
  } else if (kind === "dcdc") {
    const [v, w] = [vOf(specs.out_v), numOf(specs.w)];
    rating = v && w ? `${v}V${w}` : v ? `${v}V` : "";
  } else if (kind === "cluster" || kind === "cluster_c") {
    rating = specs.display_type ?? "";          // size stays a spec, not a name segment
  } else if (kind === "cell") {
    rating = `${specs.chem ?? ""}${numOf(specs.ah)}`;
  }
  if (rating) parts.push(rating);
  if (specs.has_ble === "true") parts.push("B");
  return parts.filter(Boolean).join("-") + "-";
}

/* ---------------- manufacturer form (page + modal) ---------------- */
export function ManufacturerForm({ initial, onDone, onCancel, presetSupplies }: {
  initial?: Manufacturer | null;
  /** kinds pre-ticked when created inline from a model form */
  presetSupplies?: string[];
  /** created/updated record → caller writes it back (dropdown selection, list refresh) */
  onDone: (m: Manufacturer) => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  // MANAGE_CATALOG is internal-only (plans.py): catalog definitions are
  // platform-level and shared by every company, so one company editing them
  // changes what every other company's assets are built from. Hiding is UX;
  // require_cap on each route is the enforcement.
  const { can } = useSession();
  if (!can(CAP.MANAGE_CATALOG)) {
    return (
      <div className="cap">
        The catalogue is maintained at platform level and is read-only here.
      </div>
    );
  }

  const fb = useMutationFeedback();
  const kinds = useTemplates().data?.kinds ?? [];
  const [f, setF] = useState({
    name: initial?.name ?? "", abbr: initial?.abbr ?? "", country: initial?.country ?? "IN", supplies: initial?.supplies ?? presetSupplies ?? ([] as string[]),
    website: initial?.website ?? "", tax_id: initial?.tax_id ?? "", support_contact: initial?.support_contact ?? "",
    notes: initial?.notes ?? "",
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));
  const staged = useStagedImage();
  const save = useMutation({
    mutationFn: async () => {
      if (initial) return assetCatalog.updateManufacturer(initial.manufacturer_id, f);
      const m = await assetCatalog.createManufacturer(f);
      // logo rides along once the id exists; a failed upload never fails the create
      if (staged.blob) await assetCatalog.uploadLogo(m.manufacturer_id, staged.blob).catch(() => undefined);
      return m;
    },
    onSuccess: (m) => { fb.ok(); void qc.invalidateQueries({ queryKey: [...QK, "manufacturers"] }); onDone(m); },
    onError: fb.fail,
  });
  const refreshMfrs = () => qc.invalidateQueries({ queryKey: [...QK, "manufacturers"] });
  const grid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 };
  return (
    <div>
      <div style={grid}>
        <Field label="Name" req>
          <SearchInput className="fill" value={f.name} onChange={set("name")} placeholder="EVE Energy" autoFocus />
        </Field>
        <Field label="Abbreviation" req>
          <SearchInput value={f.abbr} onChange={set("abbr")} placeholder="AE" mono maxLength={6}
            style={{ width: "100%", minWidth: 0, textTransform: "uppercase" }} />
        </Field>
        <Field label="Country (ISO-2)">
          <SearchInput className="fill" value={f.country ?? ""} onChange={set("country")} placeholder="IN" mono maxLength={2} />
        </Field>
        <Field label="Supplies (tick all that apply)">
          <MultiSelect options={kinds.map((k) => ({ value: k.id, label: `${k.group} · ${k.label}` }))}
            value={f.supplies} onChange={(v) => setF((p) => ({ ...p, supplies: v }))}
            emptyLabel="— not specified —" clearLabel="Clear" />
        </Field>
      </div>
      <div style={{ ...grid, marginTop: 14 }}>
        <Field label="Website"><SearchInput className="fill" value={f.website ?? ""} onChange={set("website")} placeholder="https://" /></Field>
        <Field label="GSTIN / tax id"><SearchInput className="fill" value={f.tax_id ?? ""} onChange={set("tax_id")} mono /></Field>
        <Field label="Support contact"><SearchInput className="fill" value={f.support_contact ?? ""} onChange={set("support_contact")} placeholder="name · phone · email" /></Field>
      </div>
      <div style={{ marginTop: 14 }}>
        <Field label="Notes"><SearchInput className="fill" value={f.notes ?? ""} onChange={set("notes")} /></Field>
      </div>
      <div style={{ marginTop: 14 }}>
        <Field label="Logo">
          {initial ? (
            <ImageField camera={false} size={110} fit="contain" empty="no logo"
              src={initial.logo ? assetCatalog.logoUrl(initial.manufacturer_id, initial.logo.captured_at) : null}
              meta={initial.logo ? `${new Date(initial.logo.captured_at).toLocaleDateString()} · ${initial.logo.captured_by}` : undefined}
              onUpload={async (b) => { await assetCatalog.uploadLogo(initial.manufacturer_id, b); await refreshMfrs(); }}
              onRemove={async () => { await assetCatalog.deleteLogo(initial.manufacturer_id); await refreshMfrs(); }} />
          ) : (
            <ImageField camera={false} size={110} fit="contain" empty="no logo" src={staged.url}
              meta="attached — saved with the manufacturer"
              onUpload={async (b) => staged.set(b)} onRemove={async () => staged.set(null)} />
          )}
        </Field>
      </div>
      <ActionRow top={18}>
        {fb.errorNode}
        {onCancel && <button className="btn ghost" onClick={onCancel}>Cancel</button>}
        <button className="btn teal" disabled={!f.name.trim() || !f.abbr.trim() || save.isPending} onClick={() => save.mutate()}>
          {initial ? "Save changes" : "Save manufacturer"}
        </button>
      </ActionRow>
    </div>
  );
}

/* ---------------- model form (template-driven, page + modal) ---------------- */
// `mfr` is UI-only (the manufacturer-first picker's first step). It lives ON
// the row so that removing a row cannot hand its stashed maker to the row that
// shifts into its index — the bug a side-map keyed by index produced. body()
// strips it: the server's BomItem forbids unknown fields.
type BomRow = { slot: string; child_version_id: string; qty: number; mfr?: string };

function toForm(m?: CatalogVersionDetail | null) {
  const specs: Record<string, string> = {};
  for (const [k, v] of Object.entries(m?.specs ?? {})) specs[k] = v == null ? "" : String(v);
  return {
    manufacturer_id: m?.manufacturer_id ?? "", name: m?.name ?? "", specs,
    bom: (m?.bom ?? []).map((b) => ({ slot: b.slot, child_version_id: b.version_id, qty: b.qty })) as BomRow[],
    version_note: m?.version_note ?? "",
  };
}

/** One form for: new model (kind fixed or chosen from `kinds`), edit draft,
 *  new-version-from-active. Inline "+ New" beside the manufacturer picker and
 *  each BOM slot opens a STACKED modal; the created record is written back
 *  into the opener's dropdown (LIFO: only the top modal closes). */
export function ModelForm({ kinds, model, onDone, onDeleted, onCancel, crumb }: {
  /** allowed kinds; a single entry fixes the kind, several show a picker */
  kinds: string[];
  model?: CatalogVersionDetail | null;
  onDone: (m: CatalogVersionDetail) => void;
  onDeleted?: () => void;
  onCancel?: () => void;
  /** where the result lands — shown in nested modal titles */
  crumb?: string;
}) {
  const qc = useQueryClient();
  // MANAGE_CATALOG is internal-only (plans.py): catalog definitions are
  // platform-level and shared by every company, so one company editing them
  // changes what every other company's assets are built from. Hiding is UX;
  // require_cap on each route is the enforcement.
  const { can } = useSession();
  if (!can(CAP.MANAGE_CATALOG)) {
    return (
      <div className="cap">
        The catalogue is maintained at platform level and is read-only here.
      </div>
    );
  }

  const fb = useMutationFeedback();
  const { ask, dialog } = useConfirm();
  const tpl = useTemplates().data;
  const allMfrs = useManufacturers().data?.manufacturers ?? [];
  const pool = useModelPool().data?.models ?? [];

  const [kind, setKind] = useState(model?.kind ?? (kinds.length === 1 ? kinds[0] : ""));
  const [f, setF] = useState(() => toForm(model));
  useEffect(() => { setF(toForm(model)); setKind(model?.kind ?? (kinds.length === 1 ? kinds[0] : ""));
    setSuffix(model?.name?.includes("-") ? model.name.split("-").pop()! : "R1"); },
    [model?.version_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [newMfr, setNewMfr] = useState(false);
  const [suffix, setSuffix] = useState("R1");
  const [newIn, setNewIn] = useState<CatalogSlot | null>(null);   // slot whose "+ New" is open
  // manufacturer chosen for the NEW (not yet added) row of each slot; existing
  // rows carry their own `mfr`, so splices can't cross-contaminate them
  const [newRowMfr, setNewRowMfr] = useState<Record<string, string>>({});
  const staged = useStagedImage();

  // only manufacturers that list THIS kind under Supplies (the one already on
  // the model stays visible even if its Supplies changed later)
  const mfrs = allMfrs.filter((m) => (m.status === "active" && kind && m.supplies.includes(kind)) || m.manufacturer_id === f.manufacturer_id);
  const groups = (kind && tpl?.templates[kind]) || [];
  const slots = (kind && tpl?.slots[kind]) || [];
  const status = model?.status ?? "new";
  const readOnly = status === "retired";
  const invalidate = () => qc.invalidateQueries({ queryKey: [...QK] });

  const mfrAbbr = mfrs.find((m) => m.manufacturer_id === f.manufacturer_id)?.abbr ?? "";
  const kAbbr = tpl?.kinds.find((k) => k.id === kind)?.abbr;
  const nameEditable = !model || model.status === "draft";
  const genName = () => {
    if (!nameEditable) return f.name.trim();
    if (!mfrAbbr || !kind || !suffix.trim()) return "";
    return namePrefix(kind, f.specs, mfrAbbr, kAbbr) + suffix.trim();
  };
  const body = (): ModelBody => ({
    kind, manufacturer_id: f.manufacturer_id, name: genName(),
    specs: Object.fromEntries(Object.entries(f.specs).filter(([, v]) => v !== "")),
    bom: f.bom.filter((b) => b.child_version_id)
      .map(({ slot, child_version_id, qty }) => ({ slot, child_version_id, qty })),
    version_note: f.version_note || null,
  });
  const canSave = !!kind && !!f.manufacturer_id && !!genName();

  // WHO MAY BUILD WITH THIS (D-81). [] = every company.
  //
  // It lives in the FORM, not only in the Overview drawer, because this is
  // where a model is actually made and changed. Being told a control exists on
  // another tab is the same as it not existing.
  //
  // On CREATE the model has no id until it is saved, so the selection waits
  // here and is applied the moment the id exists — the same way the picture
  // rides along below.
  // NO VISIBILITY STATE HERE. It moved to Asset Catalog / Model Visibility
  // (2026-09-28), which also removed the awkward half of it: on CREATE there
  // was no model id yet, so the selection had to ride along and be applied
  // after the save — a second write that could fail on its own, after the
  // form had already reported success. A new model is now visible to every
  // company (the empty default) until somebody decides otherwise, on the page
  // that owns that decision.


  // transient save confirmation — the edit form stays open after Save changes,
  // so without this a successful save is indistinguishable from a dead click
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef<number>();
  const flashSaved = () => {
    setSaved(true);
    window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(false), 2500);
  };
  useEffect(() => () => window.clearTimeout(savedTimer.current), []);

  const create = useMutation({
    mutationFn: async (activate: boolean) => {
      const m = await assetCatalog.createModel(body());
      // picture rides along once the id exists; a failed upload never fails the create
      if (staged.blob) await assetCatalog.uploadImage(m.version_id, staged.blob).catch(() => undefined);
      // visibility likewise — but this one is NOT swallowed: a model that was
      // meant for two customers and silently landed public is worse than a
      // create that reports it half-finished.
      return activate ? assetCatalog.activate(m.version_id).catch((e) => { throw Object.assign(e, { saved: m }); }) : m;
    },
    onSuccess: (m) => { fb.ok(); flashSaved(); void invalidate(); onDone(m); },
    onError: (e: Error & { saved?: CatalogVersionDetail }) => {
      fb.fail(e);
      // the draft exists even though activation failed — hand it to the opener
      if (e.saved) { void invalidate(); onDone(e.saved); }
    },
  });
  const saveDraft = useMutation({
    mutationFn: async (activate: boolean) => {
      const m = await assetCatalog.updateDraft(model!.version_id, body());
      return activate ? assetCatalog.activate(m.version_id) : m;
    },
    onSuccess: (m) => { fb.ok(); flashSaved(); void invalidate(); onDone(m); },
    // the draft save half may have persisted before activation refused — refresh
    onError: (e) => { fb.fail(e); void invalidate(); },
  });
  const retire = useMutation({
    mutationFn: () => assetCatalog.retire(model!.version_id),
    onSuccess: (m) => { fb.ok(); void invalidate(); onDone(m); }, onError: fb.fail,
  });
  const del = useMutation({
    mutationFn: () => assetCatalog.deleteDraft(model!.version_id),
    onSuccess: () => { fb.ok(); void invalidate(); onDeleted?.(); }, onError: fb.fail,
  });
  const busy = create.isPending || saveDraft.isPending || retire.isPending || del.isPending;

  // ---- derived preview (server is the authority; this mirrors the cheap parts) ----
  const derived = useMemo(() => {
    const d: Record<string, string> = {};
    const v = parseFloat(f.specs.nom_v ?? ""), ah = parseFloat(f.specs.ah ?? "");
    if (kind === "battery" && v > 0 && ah > 0) d.kwh = `${Math.round(v * ah / 10) / 100} kWh`;
    const children = f.bom.map((b) => pool.find((p) => p.version_id === b.child_version_id)).filter(Boolean) as CatalogVersionRow[];
    const cell = children.find((c) => c.kind === "cell");
    if (cell) d.chem = cell.key_specs.split(" · ")[0];
    const dd = (f.specs.ip_dust ?? "").split(" ")[0], dw = (f.specs.ip_water ?? "").split(" ")[0];
    if (dd || dw) d.ip = `IP${dd || "X"}${dw || "X"}`;
    const src = children.filter((c) => tpl?.kinds.find((k) => k.id === c.kind)?.conn);
    if (tpl?.kinds.find((k) => k.id === kind)?.asset) {
      d.connected = src.length === 1 ? "yes" : src.length === 0 ? "no" : "INVALID — two sources";
      if (src.length === 1) d.connected_via = `${kindLabel(tpl, src[0].kind)} ${src[0].name}`;
    }
    return d;
  }, [f, kind, pool, tpl]);
  const derivedText = (fld: CatalogField) => {
    const v = derived[fld.k] ?? (model?.derived?.[fld.k] as string | number | undefined);
    return v == null ? "—" : String(v);
  };

  const setSpec = (k: string, v: string) => setF((p) => {
    const specs = { ...p.specs, [k]: v };
    // unticking BLE takes its dependent FOTA options with it (mirror of the
    // server rule in clean_specs) — a hidden stale tick must never be sent
    if (k === "has_ble" && v !== "true") {
      delete specs.fota_host_ble; delete specs.fota_ble_4g; delete specs.fota_ble_phone;
    }
    return { ...p, specs };
  });
  const setBom = (rows: BomRow[]) => setF((p) => ({ ...p, bom: rows }));

  const rowGrid: React.CSSProperties = {
    display: "grid", gridTemplateColumns: "minmax(0,220px) minmax(0,1fr) 70px", gap: 10,
    alignItems: "center", padding: "7px 0", borderBottom: "1px solid var(--line)",
  };
  const lbl = (fld: CatalogField) => (
    <span title={fld.help} style={{ fontSize: 12.5, color: "var(--t-mute)" }}>
      {fld.label}{fld.req && <span style={{ color: "var(--orange)" }}> *</span>}
      {fld.k.startsWith("fota_") && fld.help && (
        <div className="mono" style={{ fontSize: 11, color: "var(--t-dim)" }}>{fld.help}</div>
      )}
    </span>
  );
  const input = (fld: CatalogField) => {
    const v = f.specs[fld.k] ?? "";
    if (fld.type === "derived") {
      return <span className="mono" style={{ fontSize: 12.5, color: "var(--t-bright)" }}>{derivedText(fld)}
        <span style={{ color: "var(--t-dim)", fontSize: 11 }}> · derived{fld.from ? ` from ${fld.from}` : ""}</span></span>;
    }
    if (fld.type === "bool") {
      return (
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, cursor: readOnly ? "default" : "pointer", height: 30 }}>
          <input type="checkbox" disabled={readOnly} checked={v === "true"}
            onChange={(e) => setSpec(fld.k, e.target.checked ? "true" : "")} />
          <span className="muted">{v === "true" ? "yes" : "no"}</span>
        </label>
      );
    }
    if (fld.type === "select") {
      return <Select className="fill" value={v} disabled={readOnly} onChange={(e) => setSpec(fld.k, e.target.value)}>
        <option value="">—</option>{fld.opts?.map((o) => <option key={o} value={o}>{o}</option>)}</Select>;
    }
    return <SearchInput className="fill" value={v} disabled={readOnly} type={fld.type === "num" ? "number" : "text"} mono={fld.type === "num"}
      step={fld.type === "num" ? (fld.step ?? "any") : undefined} min={fld.type === "num" ? 0 : undefined}
      placeholder={fld.ph} onChange={(e) => setSpec(fld.k, e.target.value)} />;
  };

  return (
    <div>
      {/* identity */}
      <div className="section-label">Identity</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginBottom: 18 }}>
        {kinds.length > 1 && (
          <Field label="Kind" req>
            <KindSelect kinds={tpl?.kinds ?? []} only={kinds} value={kind}
              onChange={(k) => { setKind(k); setF((p) => ({ ...p, specs: {}, bom: [] })); }}
              style={{ width: "100%", minWidth: 0 }} />
          </Field>
        )}
        <Field label="Manufacturer" req>
          <div style={{ display: "flex", gap: 6 }}>
            <Select value={f.manufacturer_id} disabled={readOnly || !kind} onChange={(e) => setF((p) => ({ ...p, manufacturer_id: e.target.value }))}
              style={{ flex: 1, minWidth: 0 }}>
              <option value="">{!kind ? "— choose kind first —" : mfrs.length ? "— choose —" : `— none supplies ${kindLabel(tpl, kind)} — add one —`}</option>
              {mfrs.map((m) => <option key={m.manufacturer_id} value={m.manufacturer_id}>{m.abbr ? `${m.abbr} — ${m.name}` : m.name}</option>)}
            </Select>
            {!readOnly && <button className="btn ghost" disabled={!kind} onClick={() => setNewMfr(true)} title="Add a manufacturer without leaving this form">+ New</button>}
          </div>
        </Field>
        {model && (
          <Field label="Version">
            <div style={{ display: "flex", gap: 8, alignItems: "center", height: 34 }}>
              <span className="mono">v{model.version}</span><ModelChip st={model.status} />
            </div>
          </Field>
        )}
      </div>

      {/* the technical name gets a full-width row — the prefix is long */}
      <div style={{ marginTop: 14, marginBottom: 18 }}>
        {(() => {
          const prefix = mfrAbbr && kind ? namePrefix(kind, f.specs, mfrAbbr, kAbbr) : "";
          const ruleTitle = prefix ? nameRuleTitle(mfrAbbr, kAbbr ?? kind.toUpperCase(), f.specs.has_ble === "true") : undefined;
          if (!nameEditable) {
            return (
              <Field label="Model name (fixed — versions are immutable)">
                <span className="mono" style={{ fontSize: 12.5, color: "var(--t-bright)", display: "inline-block",
                  background: "var(--surface)", border: "1px solid var(--line2)", borderRadius: 8, padding: "7px 12px" }}>{f.name}</span>
              </Field>
            );
          }
          if (!prefix) {
            return (
              <Field label="Model name (generated)" req>
                <span style={{ fontSize: 12.5, color: "var(--t-mute)" }}>
                  Choose the kind and a manufacturer first — the name is assembled from them, the specs and the ticks;
                  you type only the final segment. A manufacturer without an abbreviation must get one on the Manufacturers page.
                </span>
              </Field>
            );
          }
          return (
            <Field label="Model name (generated — you type only the last segment)" req>
              <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
                <span className="mono" title={ruleTitle}
                  style={{ fontSize: 12.5, color: "var(--t-bright)", background: "var(--surface)", border: "1px solid var(--line2)",
                    borderRadius: 8, padding: "8px 14px", whiteSpace: "nowrap", minWidth: 220, textAlign: "left" }}>{prefix}</span>
                <SearchInput mono value={suffix} placeholder="R1" maxLength={12}
                  style={{ width: 180, minWidth: 0 }}
                  title="the one segment you type: hardware revision (R1, R2...)" onChange={(e) => setSuffix(e.target.value)} />
                <span className="mono" style={{ fontSize: 12, color: "var(--t-mute)", whiteSpace: "nowrap" }}>= {prefix}{suffix || "…"}</span>
              </div>
            </Field>
          );
        })()}
      </div>

      {!kind && <div className="cap">Choose a kind to load its specification template.</div>}

      {/* specs, row-wise per group. FOTA rows appear only when the model's
          facts allow them (connected kind, BLE tick) — never disabled/N-A. */}
      {groups.map((g) => {
        const hasBle = f.specs.has_ble === "true";
        const fields = g.group !== "FOTA support" ? g.fields : g.fields.filter((fld) =>
          !["fota_host_ble", "fota_ble_4g", "fota_ble_phone"].includes(fld.k) || hasBle);
        if (fields.length === 0) return null;
        return (
        <div key={g.group} style={{ marginBottom: 16 }}>
          <div className="section-label">{g.group}</div>
          <div className="panel" style={{ padding: "2px 14px" }}>
            {fields.map((fld) => (
              <div key={fld.k} style={rowGrid}>
                {lbl(fld)}
                {input(fld)}
                <span className="mono" style={{ fontSize: 11.5, color: "var(--t-dim)" }}>{fld.unit ?? ""}</span>
              </div>
            ))}
          </div>
          {g.group === "FOTA support" && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, fontSize: 12, color: "var(--t-mute)" }}>
              FOTA (derived):&nbsp;
              {([["fota_host_4g", "HOST · 4G"], ["fota_host_ble", "HOST · PHONE"], ["fota_modem_4g", "4G MODEM"],
                 ["fota_ble_4g", "BLE · 4G"], ["fota_ble_phone", "BLE · PHONE"]] as const)
                .filter(([k2]) => f.specs[k2] === "true")
                .map(([k2, label]) => <StatusChip key={k2} st="x" map={{ x: { label, color: "var(--teal)" } }} />)}
              {!["fota_host_4g", "fota_host_ble", "fota_modem_4g", "fota_ble_4g", "fota_ble_phone"].some((k2) => f.specs[k2] === "true") &&
                <StatusChip st="x" map={{ x: { label: "NONE", color: "var(--t-mute)" } }} />}
            </div>
          )}
        </div>
        );
      })}

      {/* bill of materials */}
      {slots.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div className="section-label">Bill of materials</div>
          <div className="panel" style={{ padding: "2px 14px" }}>
            {slots.map((sl) => {
              const rows = f.bom.filter((b) => b.slot === sl.slot);
              // retired models are never offered — but one already IN the BOM must stay
              // visible, or the dropdown would render blank for a real value
              const opts = pool.filter((p) => sl.allow.includes(p.kind) &&
                (p.status !== "retired" || rows.some((r) => r.child_version_id === p.version_id)));
              const setRow = (i: number, patch: Partial<BomRow>) => {
                const all = [...f.bom]; let n = -1;
                for (let j = 0; j < all.length; j++) if (all[j].slot === sl.slot && ++n === i) { all[j] = { ...all[j], ...patch }; break; }
                setBom(all);
              };
              const removeRow = (i: number) => {
                const all = [...f.bom]; let n = -1;
                for (let j = 0; j < all.length; j++) if (all[j].slot === sl.slot && ++n === i) { all.splice(j, 1); break; }
                setBom(all);
              };
              // manufacturer FIRST, then that maker's models — a flat list of
              // every model in the catalog stops being usable at real counts
              const makers = [...new Set(opts.map((p) => p.manufacturer))].sort();
              const line = (b: BomRow | null, i: number) => {
                const fromModel = b?.child_version_id
                  ? (pool.find((p) => p.version_id === b.child_version_id)?.manufacturer ?? "") : "";
                const mfr = fromModel || (b ? b.mfr ?? "" : newRowMfr[sl.slot] ?? "");
                return (
                <div key={b?.child_version_id || `new-${sl.slot}-${i}`}
                  style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.25fr)",
                    gap: 6, flex: 1, minWidth: 0 }}>
                    <Select value={mfr} style={{ minWidth: 0 }}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (b) setRow(i, { child_version_id: "", mfr: v });   // maker changed → pick again
                        else setNewRowMfr((prev) => ({ ...prev, [sl.slot]: v }));
                      }}>
                      <option value="">{makers.length ? "— manufacturer —" : "— none in catalog —"}</option>
                      {makers.map((m) => <option key={m} value={m}>{m}</option>)}
                    </Select>
                    <ModelSelect rows={opts.filter((p) => p.manufacturer === mfr)}
                      value={b?.child_version_id ?? ""}
                      placeholder={mfr ? "— model —" : "— manufacturer first —"}
                      onChange={(v) => b ? setRow(i, { child_version_id: v })
                        : v && setBom([...f.bom, { slot: sl.slot, child_version_id: v, qty: 1, mfr }])} />
                  </div>
                  {sl.qty && b && (
                    <SearchInput type="number" mono value={b.qty} min={1} title="quantity" style={{ width: 70, minWidth: 0 }}
                      onChange={(e) => setRow(i, { qty: Math.max(1, parseInt(e.target.value || "1", 10)) })} />
                  )}
                  {b && !readOnly && <button className="btn red" onClick={() => removeRow(i)} title="Remove"><Icon name="close" /></button>}
                  {!b && !readOnly && <button className="btn ghost" onClick={() => setNewIn(sl)} title="Create a model for this slot">+ New</button>}
                </div>
              );
              };
              return (
                <div key={sl.slot} style={rowGrid}>
                  <span title={sl.help} style={{ fontSize: 12.5, color: "var(--t-mute)" }}>
                    {sl.role}{sl.min > 0 && <span style={{ color: "var(--orange)" }}> *</span>}
                    {sl.help && <div style={{ fontSize: 11, color: "var(--t-dim)" }}>{sl.help}</div>}
                  </span>
                  <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                    {rows.map((b, i) => line(b, i))}
                    {rows.length < sl.max && !readOnly && line(null, rows.length)}
                  </div>
                  <span className="mono" style={{ fontSize: 11.5, color: "var(--t-dim)" }}>{sl.min}–{sl.max}</span>
                </div>
              );
            })}
          </div>
          {derived.connected === "INVALID — two sources" && (
            <div style={{ color: "var(--bad)", fontSize: 12, marginTop: 6 }}>
              Two connectivity sources — an asset may have at most one connected part OR a TCU. Activation will refuse this.
            </div>
          )}
        </div>
      )}

      {/* picture — not a spec: allowed on any status, carried into new versions */}
      {kind && (
        <div style={{ marginBottom: 16 }}>
          <div className="section-label">Picture</div>
          {model ? (
            <ImageField size={120}
              src={model.image ? assetCatalog.imageUrl(model.version_id, model.image.captured_at) : null}
              meta={model.image ? `${new Date(model.image.captured_at).toLocaleDateString()} · ${model.image.captured_by}` : undefined}
              onUpload={async (b) => { await assetCatalog.uploadImage(model.version_id, b); await invalidate(); }}
              onRemove={readOnly ? undefined : async () => { await assetCatalog.deleteImage(model.version_id); await invalidate(); }} />
          ) : (
            <ImageField size={120} src={staged.url} meta="attached — saved with the model"
              onUpload={async (b) => staged.set(b)} onRemove={async () => staged.set(null)} />
          )}
        </div>
      )}

      {/* version note + actions */}
      {kind && !readOnly && (
        <Field label="Version note">
          <SearchInput className="fill" value={f.version_note} placeholder="e.g. switched to LF32 cells; +2 Ah"
            onChange={(e) => setF((p) => ({ ...p, version_note: e.target.value }))} />
        </Field>
      )}
      {/* VISIBILITY IS NOT HERE ANY MORE (2026-09-28). It was the last
          section of this form, and Dattatray moved it out: *"I think this
          needs a separate sub page in the asset catalog instead of being a
          selection in the Product models."*

          The form describes what a model IS — voltage, chemistry, what is
          fitted inside it. Visibility describes who may USE it: a commercial
          decision, taken later, by somebody who is not editing the design,
          and sometimes on a version that is retired and read-only here. It
          lives on Asset Catalog / Model Visibility, and the Overview's
          "Visible to" button opens that page with the model already chosen. */}

      <ActionRow top={18}>
        {saved && <span className="chip ok">Saved ✓</span>}
        {fb.errorNode}
        {onCancel && <button className="btn ghost" onClick={onCancel}>Cancel</button>}
        {status === "new" && <>
          <button className="btn ghost" disabled={!canSave || busy} onClick={() => create.mutate(false)}>Save draft</button>
          <button className="btn teal" disabled={!canSave || busy} onClick={() => create.mutate(true)}>Save &amp; activate</button>
        </>}
        {status === "draft" && <>
          <button className="btn red" disabled={busy}
            onClick={() => ask(`Delete draft ${model!.name} v${model!.version}? This cannot be undone.`, () => del.mutate(), "Delete draft")}>Delete draft</button>
          <button className="btn ghost" disabled={!canSave || busy} onClick={() => saveDraft.mutate(false)}>Save changes</button>
          <button className="btn teal" disabled={!canSave || busy} onClick={() => saveDraft.mutate(true)}>Save &amp; activate</button>
        </>}
        {status === "active" && <>
          <button className="btn red" disabled={busy}
            onClick={() => ask(`Retire ${model!.name} v${model!.version}? Existing units keep pointing at it; no new units can be built on it.`, () => retire.mutate(), "Retire")}>Retire</button>
          <button className="btn teal" disabled={!canSave || busy} onClick={() => saveDraft.mutate(false)}>Save changes</button>
        </>}
        {readOnly && <span className="cap">Retired versions are read-only — open the active version of this line to change it.</span>}
      </ActionRow>
      {dialog}

      {newMfr && (
        <Modal title="New manufacturer" crumb={`→ ${crumb ?? "this model"} · Manufacturer`} onClose={() => setNewMfr(false)}>
          <ManufacturerForm onCancel={() => setNewMfr(false)} presetSupplies={kind ? [kind] : []}
            onDone={(m) => { setF((p) => ({ ...p, manufacturer_id: m.manufacturer_id })); setNewMfr(false); }} />
        </Modal>
      )}
      {newIn && (
        <Modal wide title={`New ${newIn.allow.length === 1 ? kindLabel(tpl, newIn.allow[0]) : newIn.role.toLowerCase()}`}
          crumb={`→ ${crumb ?? "this model"} · ${newIn.role}`} onClose={() => setNewIn(null)}>
          <ModelForm kinds={newIn.allow} crumb={`${crumb ?? (f.name || "this model")} · ${newIn.role}`}
            onCancel={() => setNewIn(null)}
            onDone={(m) => {
              // write-back: land the created model in the slot that opened the pop-up
              setBom([...f.bom, { slot: newIn.slot, child_version_id: m.version_id, qty: 1 }]);
              setNewIn(null);
            }} />
        </Modal>
      )}
    </div>
  );
}

/** Specs rendered read-only by template group (drawer). */
export function SpecsView({ tpl, m }: { tpl: CatalogTemplates | undefined; m: CatalogVersionDetail }) {
  const groups = tpl?.templates[m.kind] ?? [];
  const val = (fld: CatalogField): ReactNode => {
    const v = fld.type === "derived" ? m.derived?.[fld.k] : m.specs[fld.k];
    if (v == null || v === "") return <span style={{ color: "var(--t-dim)" }}>—</span>;
    return <>{String(v)}{fld.unit && fld.unit !== "n" ? ` ${fld.unit}` : ""}</>;
  };
  return (
    <>
      {groups.map((g) => (
        <div key={g.group} style={{ marginTop: 14 }}>
          <div className="section-label">{g.group}</div>
          {g.fields.map((fld) => (
            <div key={fld.k} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid var(--line)" }}>
              <span className="muted">{fld.label}</span>
              <span className="mono" style={{ color: "var(--t-bright)", textAlign: "right" }}>{val(fld)}</span>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}
